import { useEffect, useState } from "react";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import heroTransfersImg from "@/assets/hero-transfers.jpg";

interface LandingPageRow {
  id: string;
  title: string;
  slug: string;
  meta_description: string | null;
  hero_image_url: string | null;
  content: string;
}

export default function TransfersPage() {
  usePageSeo("transfers");
  const [pages, setPages] = useState<LandingPageRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Transfers | World Transfers Trips";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Reliable airport and hotel transfers across the Dominican Republic. Book your ride with World Transfers Trips.");

    supabase
      .from("landing_pages")
      .select("id, title, slug, meta_description, hero_image_url, content")
      .eq("is_published", true)
      .order("sort_order")
      .then(({ data }) => {
        setPages(data ?? []);
        setLoading(false);
      });
  }, []);

  const excerpt = (content: string) => {
    const plain = content.replace(/<[^>]*>/g, "").replace(/[#*_~`>]/g, "");
    return plain.length > 120 ? plain.slice(0, 120) + "…" : plain;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <img src={heroTransfersImg} alt="Transfers" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[hsl(210,60%,10%)]/80" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
              <p className="text-secondary font-semibold tracking-wide uppercase text-sm mb-3">50+ Popular Routes Available</p>
              <h1 className="font-display text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight">
                Explore Dominican Routes
              </h1>
              <p className="text-white/70 text-xl leading-relaxed max-w-3xl mx-auto">
                Comfortable and reliable transfers across the most beautiful destinations in the Dominican Republic.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => <div key={i} className="h-72 rounded-none bg-muted animate-pulse" />)}
            </div>
          ) : pages.length === 0 ? (
            <p className="text-center text-muted-foreground text-lg py-12">No transfer routes available yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {pages.map((page, index) => (
                <motion.div key={page.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                  <Link to={`/transfers/${page.slug}`} className="group block rounded-none overflow-hidden border border-border bg-card shadow-sm hover:shadow-lg hover:border-secondary/30 transition-all duration-300">
                    <div className="h-48 overflow-hidden bg-muted relative">
                      {page.hero_image_url ? (
                        <>
                          <img src={page.hero_image_url} alt={page.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(210,60%,10%)]/50 to-transparent" />
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-primary/5">
                          <MapPin className="w-12 h-12 text-primary/40" />
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <h2 className="text-xl font-display font-bold text-foreground mb-2 group-hover:text-primary transition-colors">{page.title}</h2>
                      <p className="text-muted-foreground text-sm mb-4 leading-relaxed">{page.meta_description || excerpt(page.content)}</p>
                      <span className="inline-flex items-center gap-1 text-secondary text-sm font-semibold group-hover:gap-2 transition-all">
                        View Route Details <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
